import _get from 'lodash/get';
import _set from 'lodash/set';
import _find from 'lodash/find';
import Unicode from 'common/utils/unicode';
import I18n from 'i18n-js';
import _uniq from 'lodash/uniq';
import md5Array from 'common/utils/md5-array';
// Not using lodash isEmpty because it returns true for numbers
import { isEmpty } from 'common/utils/data-utils';
import { enableUIV2 } from 'helper/configuration';
import { isPromoAppliedInOrderQuote } from 'helper/quote';
import { getCreateOrderIDHash } from 'helper/order';
import {
  EXCEL_ERRORS,
  REVIEW_ORDER_FIELDS,
  MAX_TEXT_LENGTH,
} from 'constants/bulk-upload-constants';
import {
  POSTPAID_BILLING_TYPE,
  SERVICE_TYPE,
  CASH,
  CASH_BY_RECIPIENT,
  CASH_BY_SENDER,
  FIELD_PLACEHOLDER,
  PAYMENT_TYPE_GRABPAY,
} from 'constants/booking-constants';
import { V2_EXCEL_TABLE_CONTENT_COLS } from 'constants/excel-constants';
import { PARCEL_CATEGORY } from 'constants/country-constants';

export const NAME_REGEX = new RegExp(`^[${Unicode.w} '"]{1,50}$`);
export const PHONE_REGEX = new RegExp(/^\+?\d{5,15}$/);

export const reviewOrdersWithQuote = (reviewOrders, quoteMap) =>
  reviewOrders.map(o => {
    const orderServiceID = _get(o, 'service.ID');
    const quotes = _get(quoteMap, `${[o.quoteRequestID]}.quotes`, []);
    const quote =
      quotes.find(q => _get(q, 'serviceID') === orderServiceID) || {};
    return {
      ...o,
      quote,
    };
  });

export const validateBookingLimit = (reviewOrders, bookingLimitValidation) => {
  if (bookingLimitValidation) {
    const currentLimit = bookingLimitValidation.currentLimit;
    return reviewOrders.map((o, idx) => {
      _set(o, 'bookingLimit.errors', []);
      if (idx >= currentLimit) {
        o.bookingLimit.errors.push(bookingLimitValidation.error.message);
      }
      return o;
    });
  }
  return reviewOrders;
};

export const getStopItineraryIndex = stop => {
  const {
    isMultipleStop,
    batchReference,
    uploadRowIndex,
    itineraryIndex,
  } = stop;

  // for UI V1, [itineraryIndex] is a string for displaying as error message prefix
  // for UI V2, [itineraryIndex] is a number for identifying error belongs to which stop
  if (!isMultipleStop || enableUIV2()) return itineraryIndex;

  return `${batchReference} - ${uploadRowIndex}`;
};

export const generateErrorMsg = (
  itineraryIndex = null,
  errorKey,
  customizedErrMsg = null,
  customizedErrType = null,
) => {
  const errorMessage =
    customizedErrMsg || I18n.t(_get(EXCEL_ERRORS, `${errorKey}.message`, ''));
  const errorType =
    customizedErrType || _get(EXCEL_ERRORS, `${errorKey}.type`, '');

  if (enableUIV2()) {
    return {
      message: errorMessage,
      type: errorType,
      itineraryIndex,
      errorKey,
    };
  }

  return {
    message: !isEmpty(itineraryIndex)
      ? `[${itineraryIndex}]: ${errorMessage}`
      : errorMessage,
    type: errorType,
    errorKey,
  };
};

export const isCODService = serviceEntity =>
  !isEmpty(serviceEntity) && !isEmpty(serviceEntity.cashOnDelivery);
export const isSameDayService = serviceEntity =>
  _get(serviceEntity, 'expressServiceID') === SERVICE_TYPE.SAMEDAY;

export const isCorpBillingPayment = paymentEntity =>
  _get(paymentEntity, 'type') === POSTPAID_BILLING_TYPE;

export const getDistanceFromLatLonInMeter = (start, end) => {
  const { latitude: lat1, longitude: lon1 } = start;
  const { latitude: lat2, longitude: lon2 } = end;
  const R = 6371; // Radius of the earth in km
  const dLat = (lat2 - lat1) * (Math.PI / 180); // delta Lat
  const dLon = (lon2 - lon1) * (Math.PI / 180); // delta Long
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // Distance in km
  return d * 1000; // return in m
};

export const getUploadSpreadsheetDataArea = () => ({
  headerRow: 0,
  dataRow: 1,
  startCol: 1,
  endCol: V2_EXCEL_TABLE_CONTENT_COLS.length - 1,
});

export const getParcelCategoryID = (parcelCategoryName, countryCode) => {
  const parcelCategories = PARCEL_CATEGORY[countryCode];
  let parcelCategoryID = '0';
  parcelCategories.forEach(categoryID => {
    if (
      I18n.t(`v2.homepage.manualOrder.category.${categoryID}`) ===
      parcelCategoryName
    ) {
      parcelCategoryID = categoryID;
    }
  });
  return parseInt(parcelCategoryID, 10);
};

export const getPaymentOptions = (
  paymentMethod = '',
  paymentMethodMap,
  serviceEntity,
) => {
  let paymentEntity = null;
  let paidBy = null;
  const isSameDay = isSameDayService(serviceEntity);
  if (paymentMethod === I18n.t(FIELD_PLACEHOLDER.CORP_BILLING)) {
    paymentEntity = paymentMethodMap[POSTPAID_BILLING_TYPE];
  } else if (paymentMethod === I18n.t(FIELD_PLACEHOLDER.GRAB_PAY)) {
    paymentEntity = paymentMethodMap[PAYMENT_TYPE_GRABPAY];
  } else if (paymentMethod === I18n.t(FIELD_PLACEHOLDER.CASH_BY_RECIPIENT)) {
    paymentEntity = {
      paymentTypeID: '',
      type: CASH,
    };
    paidBy = CASH_BY_RECIPIENT;
  } else if (paymentMethod === I18n.t(FIELD_PLACEHOLDER.CASH_BY_SENDER)) {
    paymentEntity = {
      paymentTypeID: '',
      type: CASH,
    };
    paidBy = CASH_BY_SENDER;
  } else if (isSameDay && paymentMethod.toUpperCase() === CASH) {
    paymentEntity = {
      paymentTypeID: '',
      type: CASH,
    };
    paidBy = CASH_BY_SENDER;
  } else if (paymentMethod.toUpperCase() !== CASH) {
    // upper first letter
    const redefinedPaymentMethod =
      paymentMethod.charAt(0).toUpperCase() +
      paymentMethod.toLowerCase().slice(1);
    paymentEntity = paymentMethodMap[redefinedPaymentMethod] || {};
  }
  return {
    paymentEntity,
    paidBy,
  };
};

export const getSenderRecipientInfoErrors = (
  itineraryStop,
  isRequiredOrderID,
  senderAddressCoordinates,
  recipientAddressCoordinates,
) => {
  const {
    partnerOrderID,
    sender,
    // referenceCode,
  } = itineraryStop;
  const errors = [];
  const itineraryIndex = getStopItineraryIndex(itineraryStop);

  if (isEmpty(sender.name)) {
    errors.push(generateErrorMsg(itineraryIndex, 'invalid_sender_name'));
  } else if (_get(sender, 'name', '').length > MAX_TEXT_LENGTH) {
    errors.push(
      generateErrorMsg(
        itineraryIndex,
        'sender_name_too_long',
        I18n.t(EXCEL_ERRORS.sender_name_too_long.message, {
          maxLength: MAX_TEXT_LENGTH,
        }),
        REVIEW_ORDER_FIELDS.SENDER_NAME.key,
      ),
    );
  }

  if (isEmpty(sender.phone) || !PHONE_REGEX.test(sender.phone)) {
    errors.push(generateErrorMsg(itineraryIndex, 'invalid_sender_phone'));
  } else if (_get(sender, 'phone', '').length > MAX_TEXT_LENGTH) {
    errors.push(
      generateErrorMsg(
        itineraryIndex,
        'sender_phone_too_long',
        I18n.t(EXCEL_ERRORS.sender_phone_too_long.message, {
          maxLength: MAX_TEXT_LENGTH,
        }),
        REVIEW_ORDER_FIELDS.SENDER_PHONE.key,
      ),
    );
  }

  if (isEmpty(sender.address)) {
    errors.push(generateErrorMsg(itineraryIndex, 'empty_sender_address'));
  }
  if (_get(sender, 'instruction', '').length > MAX_TEXT_LENGTH) {
    errors.push(
      generateErrorMsg(
        itineraryIndex,
        'sender_location_details_too_long',
        I18n.t(EXCEL_ERRORS.sender_location_details_too_long.message, {
          maxLength: MAX_TEXT_LENGTH,
        }),
        REVIEW_ORDER_FIELDS.SENDER_INSTRUCTION.key,
      ),
    );
  }

  if (isEmpty(itineraryStop.name)) {
    errors.push(generateErrorMsg(itineraryIndex, 'invalid_recipient_name'));
  } else if (_get(itineraryStop, 'name', '').length > MAX_TEXT_LENGTH) {
    errors.push(
      generateErrorMsg(
        itineraryIndex,
        'recipient_name_too_long',
        I18n.t(EXCEL_ERRORS.recipient_name_too_long.message, {
          maxLength: MAX_TEXT_LENGTH,
        }),
        REVIEW_ORDER_FIELDS.RECEIVER_NAME.key,
      ),
    );
  }

  if (isEmpty(itineraryStop.phone) || !PHONE_REGEX.test(itineraryStop.phone)) {
    errors.push(generateErrorMsg(itineraryIndex, 'invalid_recipient_phone'));
  } else if (_get(itineraryStop, 'phone', '').length > MAX_TEXT_LENGTH) {
    errors.push(
      generateErrorMsg(
        itineraryIndex,
        'recipient_phone_too_long',
        I18n.t(EXCEL_ERRORS.recipient_phone_too_long.message, {
          maxLength: MAX_TEXT_LENGTH,
        }),
        REVIEW_ORDER_FIELDS.RECEIVER_PHONE.key,
      ),
    );
  }

  if (isEmpty(itineraryStop.input)) {
    errors.push(generateErrorMsg(itineraryIndex, 'empty_recipient_address'));
  }
  if (_get(itineraryStop, 'instruction', '').length > MAX_TEXT_LENGTH) {
    errors.push(
      generateErrorMsg(
        itineraryIndex,
        'recipient_location_details_too_long',
        I18n.t(EXCEL_ERRORS.recipient_location_details_too_long.message, {
          maxLength: MAX_TEXT_LENGTH,
        }),
        REVIEW_ORDER_FIELDS.RECEIVER_INSTRUCTION.key,
      ),
    );
  }

  if (_get(itineraryStop, 'itemNote', '').length > MAX_TEXT_LENGTH) {
    errors.push(
      generateErrorMsg(
        itineraryIndex,
        'item_note_too_long',
        I18n.t(EXCEL_ERRORS.item_note_too_long.message, {
          maxLength: MAX_TEXT_LENGTH,
        }),
        REVIEW_ORDER_FIELDS.RECEIVER_INSTRUCTION.key,
      ),
    );
  }

  if (_get(itineraryStop, 'referenceCode', '').length > MAX_TEXT_LENGTH) {
    errors.push(
      generateErrorMsg(
        itineraryIndex,
        'reference_code_too_long',
        I18n.t(EXCEL_ERRORS.reference_code_too_long.message, {
          maxLength: MAX_TEXT_LENGTH,
        }),
        REVIEW_ORDER_FIELDS.RECEIVER_INSTRUCTION.key,
      ),
    );
  }

  // TODO: Check referenceCode instead for V2 when BE change is ready
  if (isRequiredOrderID && isEmpty(partnerOrderID)) {
    errors.push(generateErrorMsg(itineraryIndex, 'require_partner_order_id'));
  }

  // allow round trip as long as there is at least 1 more stops in between
  if (
    itineraryIndex === 1 &&
    senderAddressCoordinates &&
    recipientAddressCoordinates
  ) {
    const distance = getDistanceFromLatLonInMeter(
      senderAddressCoordinates,
      recipientAddressCoordinates,
    );
    if (distance === 0) {
      errors.push(generateErrorMsg(itineraryIndex, 'zero_distance'));
    }
  }

  return errors;
};

// [order] could be a single stop order, or a stop in a MSD order
export const getParcelInfoErrors = order => {
  const {
    weight,
    parcelCategory,
    // vehicleType,
  } = order;
  const errors = [];
  const itineraryIndex = getStopItineraryIndex(order);

  if (isEmpty(weight)) {
    errors.push(generateErrorMsg(itineraryIndex, 'empty_weight'));
  } else if (isNaN(weight) || weight < 0) {
    errors.push(generateErrorMsg(itineraryIndex, 'invalid_weight'));
  }

  if (isEmpty(parcelCategory)) {
    errors.push(generateErrorMsg(itineraryIndex, 'empty_parcel_category'));
  }

  // if (isEmpty(vehicleType)) {
  //   errors.push(generateErrorMsg(itineraryIndex, 'empty_vehicle_type'));
  // }
  return errors;
};

export const getLocationServicePaymentErrors = (
  order, // could be a single stop order, or a stop in a MSD order
  serviceEntity,
  paymentEntity,
  itemAmount,
  senderAddressCoordinates,
  recipientAddressCoordinates,
) => {
  const errors = [];
  const itineraryIndex = getStopItineraryIndex(order);

  if (isEmpty(serviceEntity)) {
    errors.push(generateErrorMsg(itineraryIndex, 'invalid_service_type'));
  }

  if (isEmpty(paymentEntity)) {
    errors.push(generateErrorMsg(itineraryIndex, 'invalid_payment_method'));
  }

  if (!isEmpty(itemAmount)) {
    if (!isCODService(serviceEntity)) {
      errors.push(generateErrorMsg(itineraryIndex, 'cod_not_required'));
    } else if (isNaN(itemAmount) || itemAmount < 0) {
      errors.push(generateErrorMsg(itineraryIndex, 'cod_value_invalid'));
    }
  }

  if (!senderAddressCoordinates) {
    errors.push(generateErrorMsg(itineraryIndex, 'invalid_sender_address'));
  }

  if (!recipientAddressCoordinates) {
    errors.push(generateErrorMsg(itineraryIndex, 'invalid_recipient_address'));
  }

  return errors;
};

// Get MSD order recipient stops (elements in itinerary except for the first item, which is for sender)
export const getRecipientStops = order => {
  const { itinerary } = order;
  if (!isEmpty(itinerary)) return itinerary.slice(1);
  return [];
};

export const getMSDOrderErrorsUIV1 = order => {
  const errors = [];
  const { isMultipleStop } = order;
  if (!isMultipleStop) return errors;

  const stops = getRecipientStops(order);
  if (isEmpty(stops)) return errors;

  const seqNumbers = [];
  const duplicateSeqNumbers = [];
  const senderNames = [];
  const senderPhones = [];
  const senderAddresses = [];
  const serviceTypes = [];
  const vehicleTypes = [];
  const paymentMethods = [];
  const promos = [];

  stops.forEach((stop, index) => {
    const {
      sequenceNumber,
      sender,
      vehicleType,
      serviceType,
      paymentMethod,
      promo,
    } = stop;
    const itineraryIndex = getStopItineraryIndex(stop);
    senderNames.push(sender.name);
    senderPhones.push(sender.phone);
    senderAddresses.push([sender.input, sender.instruction].join(' '));
    serviceTypes.push(serviceType);
    vehicleTypes.push(vehicleType);
    paymentMethods.push(paymentMethod);
    promos.push(promo);

    // Temporarily Disabling MAX_DROPOFF_LOCATIONS check
    // https://jira.grab.com/browse/GFOM-11721
    // if (index >= MAX_DROPOFF_LOCATIONS) {
    //   errors.push(
    //     generateErrorMsg(itineraryIndex, 'exceed_maximum_recipients'),
    //   );
    // }

    if (isEmpty(sequenceNumber)) {
      errors.push(generateErrorMsg(itineraryIndex, 'empty_sequence_number'));
    } else if (isNaN(sequenceNumber) || sequenceNumber <= 0) {
      errors.push(generateErrorMsg(itineraryIndex, 'invalid_sequence_number'));
    } else if (seqNumbers.includes(sequenceNumber)) {
      duplicateSeqNumbers.push(sequenceNumber);
    } else {
      seqNumbers.push(sequenceNumber);
    }
  });

  const duplicateSeqNumsStr = _uniq(duplicateSeqNumbers).join(', ');
  const uniqSenderNames = _uniq(senderNames);
  const uniqSenderPhones = _uniq(senderPhones);
  const uniqSenderAddresses = _uniq(senderAddresses);
  const uniqServiceTypes = _uniq(serviceTypes);
  const uniqVehicleTypes = _uniq(vehicleTypes);
  const uniqPaymentMethods = _uniq(paymentMethods);
  const uniqPromos = _uniq(promos);

  if (!isEmpty(duplicateSeqNumsStr)) {
    errors.push(
      generateErrorMsg(
        null,
        null,
        `Duplicate sequence numbers [ ${duplicateSeqNumsStr} ]`,
        REVIEW_ORDER_FIELDS.SEQUENCE_NUMBER.key,
      ),
    );
  }

  if (uniqSenderNames.length > 1) {
    const names = uniqSenderNames.join(', ');
    errors.push(
      generateErrorMsg(
        null,
        'different_sender_name',
        `Different sender names [ ${names} ]`,
        REVIEW_ORDER_FIELDS.SENDER_NAME.key,
      ),
    );
  }

  if (uniqSenderPhones.length > 1) {
    errors.push(
      generateErrorMsg(
        null,
        'different_sender_phone',
        `Different sender phone numbers [ ${uniqSenderPhones.join(', ')} ]`,
        REVIEW_ORDER_FIELDS.SENDER_PHONE.key,
      ),
    );
  }

  if (uniqSenderAddresses.length > 1) {
    errors.push(
      generateErrorMsg(
        null,
        'different_sender_address',
        `Different sender address [ ${uniqSenderAddresses.join(', ')} ]`,
        REVIEW_ORDER_FIELDS.SENDER_ADDRESS.key,
      ),
    );
  }

  if (uniqServiceTypes.length > 1) {
    errors.push(
      generateErrorMsg(
        null,
        null,
        `Different service types [ ${uniqServiceTypes.join(', ')} ]`,
        REVIEW_ORDER_FIELDS.SERVICE_TYPE.key,
      ),
    );
  }

  if (uniqVehicleTypes.length > 1) {
    const vehicles = uniqVehicleTypes.join(', ');
    errors.push(
      generateErrorMsg(
        null,
        null,
        `Different vehicle types [ ${vehicles} ]`,
        REVIEW_ORDER_FIELDS.VEHICLE_TYPE.key,
      ),
    );
  }

  if (uniqPaymentMethods.length > 1) {
    errors.push(
      generateErrorMsg(
        null,
        null,
        `Different payment methods [ ${uniqPaymentMethods.join(', ')} ]`,
        REVIEW_ORDER_FIELDS.PAYMENT.key,
      ),
    );
  }

  if (uniqPromos.length > 1) {
    errors.push(
      generateErrorMsg(
        null,
        null,
        `Different promo codes [ ${uniqPromos.join(', ')} ]`,
        REVIEW_ORDER_FIELDS.PROMO.key,
      ),
    );
  }

  return errors;
};

export const getMSDOrderErrorsUIV2 = (order, serviceTypeMap) => {
  const errors = [];
  const { isMultipleStop } = order;
  if (!isMultipleStop) return errors;

  const serviceID = _get(order, 'itinerary[1].service.ID');
  const selectedServiceType = _find(
    serviceTypeMap,
    service => service.ID === serviceID,
  );
  const maxDropoffLocations = _get(selectedServiceType, 'maxMSDStops', 1);

  const stops = getRecipientStops(order);
  if (isEmpty(stops)) return errors;

  const existingValidSeqNums = [];
  const senderNames = [];
  const senderPhones = [];
  const senderAddresses = [];
  const serviceTypes = [];
  const vehicleTypes = [];
  const paymentMethods = [];
  const promos = [];

  stops.forEach((stop, index) => {
    const itineraryIndex = getStopItineraryIndex(stop);
    const {
      sequenceNumber,
      sender,
      vehicleType,
      serviceType,
      paymentMethod,
      promo,
    } = stop;
    senderNames.push(sender.name);
    senderPhones.push(sender.phone);
    senderAddresses.push([sender.input, sender.instruction].join(' '));
    serviceTypes.push(serviceType);
    vehicleTypes.push(vehicleType);
    paymentMethods.push(paymentMethod);
    promos.push(promo);

    if (index >= maxDropoffLocations) {
      errors.push(
        generateErrorMsg(
          itineraryIndex,
          'exceed_maximum_recipients',
          I18n.t(EXCEL_ERRORS.exceed_maximum_recipients.message, {
            maxDropoffLocations,
          }),
          EXCEL_ERRORS.exceed_maximum_recipients.type,
        ),
      );
    }

    if (isEmpty(sequenceNumber)) {
      errors.push(generateErrorMsg(itineraryIndex, 'empty_sequence_number'));
    } else if (isNaN(sequenceNumber) || sequenceNumber <= 0) {
      errors.push(generateErrorMsg(itineraryIndex, 'invalid_sequence_number'));
    } else if (existingValidSeqNums.includes(sequenceNumber)) {
      errors.push(
        generateErrorMsg(itineraryIndex, 'duplicate_sequence_number'),
      );
    } else {
      existingValidSeqNums.push(sequenceNumber);
    }
  });

  stops.forEach(stop => {
    const itineraryIndex = getStopItineraryIndex(stop);
    if (_uniq(senderNames).length > 1) {
      errors.push(generateErrorMsg(itineraryIndex, 'different_sender_name'));
    }

    if (_uniq(senderPhones).length > 1) {
      errors.push(generateErrorMsg(itineraryIndex, 'different_sender_phone'));
    }

    if (_uniq(senderAddresses).length > 1) {
      errors.push(generateErrorMsg(itineraryIndex, 'different_sender_address'));
    }

    if (_uniq(serviceTypes).length > 1) {
      errors.push(generateErrorMsg(itineraryIndex, 'different_service_type'));
    }

    if (_uniq(vehicleTypes).length > 1) {
      errors.push(generateErrorMsg(itineraryIndex, 'different_vehicle_type'));
    }

    if (_uniq(paymentMethods).length > 1) {
      errors.push(generateErrorMsg(itineraryIndex, 'different_payment_type'));
    }

    if (_uniq(promos).length > 1) {
      errors.push(generateErrorMsg(itineraryIndex, 'different_promo_code'));
    }
  });

  return errors;
};

export const validatePaymentMethods = (order, isCashlessPaymentDisabled) => {
  const errors = [];
  getRecipientStops(order).forEach((stop, index) => {
    const payment = _get(stop, 'payment');
    if (!payment) return;
    if (payment.type === 'CASH') return;
    const disabledPayment =
      isCashlessPaymentDisabled || (payment.paymentTypeID && !payment.enabled);
    if (disabledPayment) {
      errors.push(
        generateErrorMsg(stop.itineraryIndex, 'payment_method_disabled'),
      );
    }
  });
  return errors;
};

export const getMSDOrderErrors = (order, serviceTypeMap) =>
  enableUIV2()
    ? getMSDOrderErrorsUIV2(order, serviceTypeMap)
    : getMSDOrderErrorsUIV1(order);

export const getItineraryStopValidationErrors = (
  itineraryStop,
  isRequiredOrderID,
  serviceTypeMap,
  addressMap,
  paymentMethodMap,
) => {
  const { serviceType, paymentMethod, itemAmount, sender } = itineraryStop;

  const serviceEntity = serviceTypeMap[serviceType] || {};

  const { paymentEntity } = getPaymentOptions(
    paymentMethod,
    paymentMethodMap,
    serviceEntity,
  );
  const senderAddressEntity = addressMap[sender.input] || {
    address: {},
    grabtaxi: {},
  };
  const recipientAddressEntity = addressMap[itineraryStop.input] || {
    address: {},
    grabtaxi: {},
  };

  // validate order data by category & get corresponding errors
  const senderRecipientInfoErrors = getSenderRecipientInfoErrors(
    itineraryStop,
    isRequiredOrderID,
    senderAddressEntity.latlng,
    recipientAddressEntity.latlng,
  );

  const locationServicePaymentErrors = getLocationServicePaymentErrors(
    itineraryStop,
    serviceEntity,
    paymentEntity,
    itemAmount,
    senderAddressEntity.latlng,
    recipientAddressEntity.latlng,
  );

  const parcelInfoErrors = getParcelInfoErrors(itineraryStop);

  return {
    senderRecipientInfoErrors,
    locationServicePaymentErrors,
    parcelInfoErrors,
  };
};

export const getQuoteRequestID = order => {
  const { itinerary } = order;
  const generationArr = itinerary.reduce(
    (acc, itineraryItem, _index) =>
      acc.concat([
        itineraryItem.address,
        itineraryItem.keywords,
        itineraryItem.instruction,
        itineraryItem.name,
        JSON.stringify(itineraryItem.coordinates),
      ]),
    [Date.now().toString()], // current timestamp, ensure ID is different each time
  );
  return md5Array(generationArr);
};

export const getAddressEntity = (addressMap, addressKey) =>
  addressMap[addressKey] || {
    address: {},
    grabtaxi: {},
  };

const serviceAndPaymentMeta = (orderItem, paymentMethodMap, serviceTypeMap) => {
  const { paymentMethod, serviceType } = orderItem;
  const serviceEntity = serviceTypeMap[serviceType] || {};
  const { paymentEntity, paidBy } = getPaymentOptions(
    paymentMethod,
    paymentMethodMap,
    serviceEntity,
  );
  const paymentType = _get(paymentEntity, 'type');
  const paymentEnabled =
    paymentType === 'CASH' || _get(paymentEntity, 'enabled');
  return {
    service: {
      ID: _get(serviceEntity, 'ID'),
      expressServiceID: _get(serviceEntity, 'expressServiceID'),
      name: _get(serviceEntity, 'name'),
    },
    payment: {
      paymentTypeID: _get(paymentEntity, 'paymentTypeID'),
      refNumber: _get(paymentEntity, 'refNumber'),
      type: paymentType,
      enabled: paymentEnabled,
    },
    paidBy,
  };
};

// add sender step and other meta into order itinerary
// return updated order object
const updateOrderItinerary = (
  order,
  addressMap,
  paymentMethodMap,
  serviceTypeMap,
) => {
  const { isMultipleStop } = order;
  // For single stop pass [order] as single stop
  const orderStops = isMultipleStop ? order.itinerary : [order];

  const senderItineraries = orderStops.map(stop => {
    const sender = stop.sender;
    const senderAddressEntity = getAddressEntity(addressMap, stop.origin);

    return {
      name: sender.name,
      phone: sender.phone,
      instruction: sender.instruction,
      coordinates: senderAddressEntity.latlng,
      address: senderAddressEntity.address.combined_address,
      keywords: senderAddressEntity.address.name,
      input: senderAddressEntity.input,
      cityID: senderAddressEntity.grabtaxi.city_id,
      poiID: senderAddressEntity.id,
    };
  });

  // for MSD order, only assign pickUpStop data when all dropoff stop's sender data are valid & identical
  // before validation, only assign itineraryIndex to pickUpStop
  const pickUpStop = isMultipleStop
    ? { itineraryIndex: 0 }
    : {
        ...senderItineraries[0],
        itineraryIndex: 0,
      };

  const newItinerary = [pickUpStop];

  orderStops.forEach((stop, index) => {
    const { recipient, destination } = stop;
    const recipientAddressEntity = getAddressEntity(addressMap, destination);
    const stopCopy = { ...stop };
    delete stopCopy.origin;
    delete stopCopy.destination;
    delete stopCopy.recipient;
    newItinerary.push({
      ...stopCopy,
      // update sender data in each dropOff stop
      sender: senderItineraries[index],
      itineraryIndex: index + 1, // itineraryIndex 0 for pickup stop, +1 for drop-off stops
      name: recipient.name,
      phone: recipient.phone,
      instruction: recipient.instruction,
      coordinates: recipientAddressEntity.latlng,
      address: recipientAddressEntity.address.combined_address,
      keywords: recipientAddressEntity.address.name,
      input: recipientAddressEntity.input,
      cityID: recipientAddressEntity.grabtaxi.city_id,
      poiID: recipientAddressEntity.id,
      // assign service and payment meta to each stop
      ...serviceAndPaymentMeta(stop, paymentMethodMap, serviceTypeMap),
    });
  });

  const orderCopy = { ...order };
  // clean up: remove attributes that have been assigned to itinerary
  delete orderCopy.origin;
  delete orderCopy.destination;
  delete orderCopy.sender;
  delete orderCopy.recipient;

  return {
    ...orderCopy,
    itinerary: newItinerary,
  };
};

// Check whether order has errors that would affect getting a valid quote
// Refer to [getFetchQuoteParams] for getting quote params and hence concerned errors
export const hasQuoteParamError = concernedErrors => {
  // Exclude errors do not affect getting a valid quote
  const quoteParamErrors = concernedErrors.filter(error =>
    _get(EXCEL_ERRORS, `[${error.errorKey}].isSkipQuoteError`),
  );
  return !isEmpty(quoteParamErrors);
};

export const getOrderErrors = (
  order,
  isRequiredOrderID,
  serviceTypeMap,
  addressMap,
  paymentMethodMap,
  isCashlessPaymentDisabled,
) => {
  const { isMultipleStop } = order;
  let totalErrors = [];
  if (isMultipleStop) {
    totalErrors = totalErrors.concat(getMSDOrderErrors(order, serviceTypeMap));
  }
  totalErrors = totalErrors.concat(
    validatePaymentMethods(order, isCashlessPaymentDisabled),
  );
  getRecipientStops(order).forEach(stop => {
    const {
      senderRecipientInfoErrors,
      locationServicePaymentErrors,
      parcelInfoErrors,
    } = getItineraryStopValidationErrors(
      stop,
      isRequiredOrderID,
      serviceTypeMap,
      addressMap,
      paymentMethodMap,
    );
    totalErrors = totalErrors.concat(
      senderRecipientInfoErrors,
      locationServicePaymentErrors,
      parcelInfoErrors,
    );
  });
  return totalErrors;
};

export const getFetchQuoteParams = (skipFetchQuote, fetchOptions) => {
  if (skipFetchQuote) return null;

  const { serviceID, validatedOrder } = fetchOptions;
  const { promo, payment, quoteRequestID, itinerary } = validatedOrder;
  const params = {
    quoteRequestID,
    serviceID,
    paymentMethodID: payment.paymentTypeID,
    paymentMethodType: payment.type,
    itinerary: itinerary.map(stop => ({
      coordinates: stop.coordinates,
      address: stop.address,
      keywords: stop.keywords,
    })),
  };
  if (!isEmpty(promo)) params.promoCode = promo;
  return params;
};

export const hasDifferentSenderError = concernedErrors => {
  const differentSenderErrors = concernedErrors.filter(e =>
    [
      'different_sender_name',
      'different_sender_phone',
      'different_sender_address',
    ].includes(e.errorKey),
  );
  return !isEmpty(differentSenderErrors);
};

// validate raw delivery parsed from spreadsheet
// return review order object with updated errors & new delivery in fetch quote params
export const validateReviewOrder = ({
  order,
  addressMap,
  serviceTypeMap,
  paymentMethodMap,
  initialValidationMeta,
  isCashlessPaymentDisabled = false,
}) => {
  const firstValidation = !isEmpty(initialValidationMeta);
  const { isMultipleStop } = order;

  let validatedOrder = { ...order };
  const referenceCode = _get(order, 'itinerary[0].referenceCode');
  if (referenceCode) validatedOrder.referenceCode = referenceCode;
  const isRequiredOrderID = firstValidation
    ? initialValidationMeta.isRequiredOrderID
    : order.isRequiredOrderID;

  if (firstValidation) {
    validatedOrder = updateOrderItinerary(
      validatedOrder,
      addressMap,
      paymentMethodMap,
      serviceTypeMap,
    );
  }

  const totalErrors = getOrderErrors(
    validatedOrder,
    isRequiredOrderID,
    serviceTypeMap,
    addressMap,
    paymentMethodMap,
    isCashlessPaymentDisabled,
  );

  // for MSD order, only assign pickUp stop data in itinerary when it is identical for all stops
  if (isMultipleStop && !hasDifferentSenderError(totalErrors)) {
    // update pickUp stop in itinerary by copying sender data in a dropOff stop
    validatedOrder.itinerary[0] = {
      ...validatedOrder.itinerary[0],
      ...validatedOrder.itinerary[1].sender,
    };
  }

  validatedOrder = {
    ...validatedOrder,
    errors: totalErrors,
    quoteRequestID: getQuoteRequestID(validatedOrder),
  };

  if (firstValidation) {
    const { orderId, cityID, currencyCode } = initialValidationMeta;
    validatedOrder = {
      ...validatedOrder,
      isRequiredOrderID,
      orderId,
      cityID,
      currencyCodeCOD: currencyCode,
      locationEditable: true,
    };
  }

  const skipFetchQuote = hasQuoteParamError(totalErrors);

  // assign essential data to order object from itinerary,
  // when these data are error free & identical for each stop
  if (!skipFetchQuote) {
    const stopData = validatedOrder.itinerary[1];
    validatedOrder = {
      ...validatedOrder,
      paidBy: stopData.paidBy,
      partnerOrderID: stopData.partnerOrderID,
      payment: stopData.payment,
      paymentMethod: stopData.paymentMethod,
      service: stopData.service,
      serviceType: stopData.serviceType,
      vehicleType: stopData.vehicleType,
      promo: stopData.promo,
    };
  }

  const { serviceType } = validatedOrder;
  const serviceEntity = serviceTypeMap[serviceType] || {};

  const newQuoteDelivery = getFetchQuoteParams(skipFetchQuote, {
    serviceID: serviceEntity.ID,
    validatedOrder,
  });

  return {
    validatedOrder,
    newQuoteDelivery,
    serviceEntity,
    totalErrors: totalErrors.map(error => error.message),
  };
};

export const getUploadPercentage = (
  currentPercentage,
  limit,
  deliveriesLength,
) => {
  const percentage = Math.floor(deliveriesLength / limit);
  return Math.min(100, Math.floor(currentPercentage * percentage));
};

export const transformErrorDeliveries = (
  errorsDeliveries,
  transformedOrders,
) => {
  let countInvalidOrderQty = 0;
  const newErrorDeliveries = transformedOrders.filter(order => {
    if (isEmpty(errorsDeliveries[order.quoteRequestID])) return false;
    errorsDeliveries[order.quoteRequestID].errors.filter(error => {
      if (error.errorCode === 1019 || error.errorCode === 1044) {
        countInvalidOrderQty += 1;
        order.errors = errorsDeliveries[order.quoteRequestID].errors; // eslint-disable-line no-param-reassign
      }
      return order;
    });
    return order;
  });
  return {
    newErrorDeliveries,
    countInvalidOrderQty,
  };
};

export const getNumberOfSuccessfulOrder = quantitySuccessfulOrder =>
  quantitySuccessfulOrder + 1;

export const getQuoteEntity = (order, quoteMap) => {
  const matchedQuotes = quoteMap[order.quoteRequestID];
  if (isEmpty(matchedQuotes)) return {};

  const matchedQuote =
    matchedQuotes.quotes.find(quo => quo.serviceID === order.service.ID) || {};
  return matchedQuote;
};

export const getDeliveryDetailsV2 = (
  order,
  quoteMap,
  countryCode,
  cityCode,
  userInfo,
) => {
  const {
    quoteRequestID,
    partnerOrderID,
    itinerary,
    service,
    payment,
    promo,
    paidBy,
    referenceCode,
  } = order;
  const quoteEntity = getQuoteEntity(order, quoteMap);
  const steps = [];

  itinerary.forEach((stop, index) => {
    let step = {
      contact: {
        name: stop.name,
        phone: stop.phone,
        instruction: stop.instruction,
      },
      // first stop for pickup (job 0), rest for dropoff (job 1)
      jobType: index === 0 ? 0 : 1,
      location: {
        address: stop.address,
        keywords: stop.keywords,
        coordinates: stop.coordinates,
        poiID: stop.poiID,
      },
    };
    if (index !== 0) {
      const parcelCategory = _get(stop, 'parcelCategory');
      const weight = _get(stop, 'weight');
      const itemNote = _get(stop, 'itemNote');
      if (!isEmpty(parcelCategory) && !isEmpty(weight)) {
        step = {
          ...step,
          itemInfo: {
            categoryID: getParcelCategoryID(parcelCategory, countryCode),
            categoryName: parcelCategory,
            weight,
            note: itemNote,
          },
        };
      }

      const itemAmount = _get(stop, 'itemAmount');
      if (!isEmpty(itemAmount)) {
        step = {
          ...step,
          cashOnDelivery: {
            enable: true,
            amount: Number(itemAmount),
          },
        };
      }
    }
    steps.push(step);
  });

  const isPromoApplied = isPromoAppliedInOrderQuote(
    promo,
    quoteRequestID,
    quoteMap,
  );
  const serviceWithoutPromo = {
    expressServiceID: service.expressServiceID,
    quoteSignature: quoteEntity.quoteSignature,
    serviceID: service.ID,
    serviceName: service.name,
  };

  return {
    createRequestID: getCreateOrderIDHash(userInfo, itinerary[0], {
      0: itinerary[1],
    }),
    /* In GE-WEB UI 1.0 noteToDriver and partnerOrderID (referenceCode in UI 2.0)
       share same input component
       TODO: Move partnerOrderID to referenceCode once BE API change is ready
       https://grab.slack.com/archives/G016LNT7RKQ/p1611302243019300
    */
    partnerOrderID,
    noteToDriver: partnerOrderID,
    discountMeta: quoteEntity.discountMeta,
    paymentMethod: {
      id: payment.paymentTypeID,
      name: payment.refNumber,
      type: payment.type,
      refNumber: payment.refNumber,
    },
    service: {
      ...serviceWithoutPromo,
      isPromoApplied,
      promoCode: promo,
    },
    steps,
    countryCode,
    cityCode,
    paidBy,
    referenceCode,
  };
};

export const getDeliveryDetailsV1 = (
  order,
  quoteMap,
  countryCode,
  cityCode,
  userInfo,
) => {
  const {
    quoteRequestID,
    itinerary,
    service,
    payment,
    promo,
    // itemAmount,
    partnerOrderID,
    paidBy,
  } = order;
  const sender = itinerary[0];
  const recipient = itinerary[1];
  const itemAmount = _get(recipient, 'itemAmount');
  const quoteEntity = getQuoteEntity(order, quoteMap);

  const isPromoApplied = isPromoAppliedInOrderQuote(
    promo,
    quoteRequestID,
    quoteMap,
  );
  const serviceWithoutPromo = {
    expressServiceID: service.expressServiceID,
    quoteSignature: quoteEntity.quoteSignature,
    serviceID: service.ID,
    serviceName: service.name,
  };

  return {
    /* in V1, createRequestID is used to check and show notification among multiple bookings
     * -> createRequestID must be different on each booking
     * Additionally, BE does not use createRequestID for anything
    */
    createRequestID: getCreateOrderIDHash(
      userInfo,
      sender,
      {
        0: recipient,
      },
      new Date().valueOf().toString(),
    ),
    service: {
      ...serviceWithoutPromo,
      isPromoApplied,
      promoCode: promo,
    },
    /* In GE-WEB UI 1.0 noteToDriver and partnerOrderID (referenceCode in UI 2.0)
       share same input component
    */
    partnerOrderID,
    noteToDriver: partnerOrderID,
    paymentMethodID: payment.paymentTypeID,
    paymentMethodName: payment.refNumber,
    paymentMethodType: payment.type,
    sender: {
      name: sender.name,
      phone: sender.phone,
      instruction: sender.instruction,
    },
    paidBy,
    recipient: {
      name: recipient.name,
      phone: recipient.phone,
      instruction: recipient.instruction,
    },
    origin: {
      keywords: sender.keywords,
      address: sender.address,
      coordinates: sender.coordinates,
      poiID: sender.poiID,
    },
    destination: {
      keywords: recipient.keywords,
      address: recipient.address,
      coordinates: recipient.coordinates,
      poiID: recipient.poiID,
    },
    discountMeta: quoteEntity.discountMeta,
    cashOnDelivery: isEmpty(itemAmount)
      ? null
      : {
          enable: true,
          amount: Number(itemAmount),
        },
    countryCode,
    cityCode,
  };
};

export const getDeliveryDetails = (
  order,
  quoteMap,
  countryCode,
  cityCode,
  userInfo,
) => getDeliveryDetailsV2(order, quoteMap, countryCode, cityCode, userInfo);

export const updateReviewScreenState = (reviewOrders, state) => {
  const quoteMap = state && state.quoteMap;

  if (isEmpty(reviewOrders)) return [];
  let invalidOrderQty = 0;
  const updatedOrders = reviewOrders.map(order => {
    const quoteEntity = getQuoteEntity(order, quoteMap);
    let errors = [];
    const hasError = !isEmpty(quoteEntity.errors) || !isEmpty(order.errors);

    if (hasError) {
      invalidOrderQty += 1;
      errors = [].concat(
        !isEmpty(quoteEntity.errors) ? quoteEntity.errors : [],
        !isEmpty(order.errors) ? order.errors : [],
      );
    }
    return Object.assign({}, order, quoteEntity, {
      errors,
      amount: isEmpty(quoteEntity.amount) ? 0 : quoteEntity.amount,
    });
  });
  return {
    updatedOrders,
    invalidOrderQty,
    validOrderQty: updatedOrders.length - invalidOrderQty,
  };
};

export const isOrderWithValidQuote = (order, quoteMap) => {
  const quoteEntity = getQuoteEntity(order, quoteMap);
  return !isEmpty(quoteEntity) && !isEmpty(quoteEntity.serviceID);
};

export const haveOrderWithoutValidQuote = (orders, quoteMap) =>
  orders.some(order => !isOrderWithValidQuote(order, quoteMap));

// For UI V2 only
export const getUpdateReviewOrderPath = (
  orderIndex,
  fieldKey,
  itineraryIndex,
) => {
  const reviewOrderField = REVIEW_ORDER_FIELDS[fieldKey];
  let path;
  // lodash _isEmpty function returns true for any number
  if (reviewOrderField.inItinerary) {
    if (itineraryIndex > 0 && reviewOrderField.updateSenderInItineray) {
      // updating sender data in drop-off itinerary
      // except address, which is handled separately in [updateReviewOrderLocation]
      path = `[${orderIndex}].itinerary[${itineraryIndex}].sender.${
        reviewOrderField.orderAttribute
      }`;
    } else {
      // updating sender data in pick-up itinerary or other data in drop-off itinerary
      // except address, which is handled separately in [updateReviewOrderLocation]
      path = `[${orderIndex}].itinerary[${itineraryIndex}].${
        reviewOrderField.orderAttribute
      }`;
    }
  } else {
    path = `[${orderIndex}].${reviewOrderField.orderAttribute}`;
  }
  return path;
};

export const getUpdateErrFieldOriginalValue = (
  orders,
  orderIndex,
  fieldKey,
  itineraryIndex,
) => {
  const originalValuePath = getUpdateReviewOrderPath(
    orderIndex,
    fieldKey,
    itineraryIndex,
  );
  return _get(orders, originalValuePath);
};

export const updateReviewOrderLocation = ({
  newLocation,
  updatingOrderMeta,
  serviceTypeMap,
  addressMap,
  paymentMethodMap,
}) => {
  const { order, itineraryIndex } = updatingOrderMeta;

  let newReviewOrder = { ...order };
  const { isMultipleStop } = order;

  // update single recipient location
  if (itineraryIndex !== 0) {
    const updatePath = `itinerary[${itineraryIndex}]`;
    const originalItineraryItem = _get(order, updatePath);
    const newItineraryItem = {
      ...originalItineraryItem,
      ...newLocation,
      input: newLocation.keywords,
    };
    newReviewOrder = _set(newReviewOrder, updatePath, newItineraryItem);
  } else {
    // update sender location => update for all recipient stops
    const { itinerary } = order;
    let currentItineraryIndex = 1;
    while (currentItineraryIndex < itinerary.length) {
      const updatePath = `itinerary[${currentItineraryIndex}].sender`;
      const originalSenderForRecipient = _get(order, updatePath);
      const newSenderForRecipient = {
        ...originalSenderForRecipient,
        ...newLocation,
        input: newLocation.keywords,
      };
      newReviewOrder = _set(newReviewOrder, updatePath, newSenderForRecipient);
      currentItineraryIndex += 1;
    }
    // For MSD order, only update sender itinerary[0] when all stop validation pass
    if (!isMultipleStop) {
      newReviewOrder = _set(newReviewOrder, 'itinerary[0]', {
        ..._get(order, 'itinerary[0]'),
        ...newLocation,
        input: newLocation.keywords,
      });
    }
  }

  return validateReviewOrder({
    order: newReviewOrder,
    addressMap,
    serviceTypeMap,
    paymentMethodMap,
  });
};

export const updateReviewOrders = ({
  orders,
  updateOrderIndex,
  fieldKey,
  newValue,
  itineraryIndex,
  orderMeta,
}) => {
  const { serviceTypeMap, addressMap, paymentMethodMap } = orderMeta;

  let newOrders = [...orders];
  let updateValue = isEmpty(newValue) ? null : newValue;
  if (updateValue && REVIEW_ORDER_FIELDS[fieldKey].dataType === 'number') {
    updateValue = Number(updateValue);
  } else if (
    updateValue &&
    REVIEW_ORDER_FIELDS[fieldKey].dataType === 'string'
  ) {
    updateValue = String(updateValue);
  }

  const updatePath = getUpdateReviewOrderPath(
    updateOrderIndex,
    fieldKey,
    itineraryIndex,
  );
  newOrders = _set(newOrders, updatePath, updateValue);

  // if updating sender info for a delivery stop in itineray,
  // should update same info in pickup stop in itinerary too
  if (REVIEW_ORDER_FIELDS[fieldKey].updateSenderInItineray) {
    const updateSenderInItineraryPath = getUpdateReviewOrderPath(
      updateOrderIndex,
      fieldKey,
      0,
    );
    newOrders = _set(newOrders, updateSenderInItineraryPath, updateValue);
  }

  const { validatedOrder, newQuoteDelivery } = validateReviewOrder({
    order: newOrders[updateOrderIndex],
    serviceTypeMap,
    addressMap,
    paymentMethodMap,
  });

  newOrders[updateOrderIndex] = validatedOrder;
  return {
    updatedReviewOrders: newOrders,
    newQuoteDelivery,
  };
};

export const getAddressSearchKeywords = order => {
  const { isMultipleStop } = order;
  if (!isMultipleStop) return [order.origin, order.destination];

  // for MSD, need to search address for each stop's pick-up & drop-off
  return order.itinerary.reduce((acc, stop, itineraryIndex) => {
    acc.push(stop.origin);
    acc.push(stop.destination);
    return acc;
  }, []);
};

export const getServiceTypeMap = serviceTypes =>
  serviceTypes.reduce((acc, service) => {
    acc[service.name] = service;
    return acc;
  }, {});

export const getPaymentMethodMap = paymentMethods =>
  !isEmpty(paymentMethods)
    ? paymentMethods.reduce((acc, payment) => {
        const key = payment.refNumber
          ? ''.concat(
              payment.type[0],
              payment.type.substr(1).toLowerCase(),
              ' ',
              payment.refNumber,
            )
          : payment.type;
        acc[key] = payment;
        return acc;
      }, {})
    : {};
